import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import {
  BadgeCheck,
  Building2,
  CheckCircle2,
  Hotel as HotelIcon,
  IndianRupee,
  Loader2,
  LogOut,
  MapPin,
  Plus,
  ShieldAlert,
  Star,
  Ticket,
  Users,
  XCircle,
} from "lucide-react";
import Logo from "@/components/Logo";
import { adminApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { asArray } from "@/lib/asArray";
import { useAuth } from "@/store/auth";
import type { Hotel, PlatformStats, VendorAdminView } from "@/types";

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [queue, setQueue] = useState<VendorAdminView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () => {
    setError(null);
    Promise.all([adminApi.stats(), adminApi.queue()])
      .then(([s, q]) => {
        setStats(s.data);
        setQueue(asArray<VendorAdminView>(q.data));
      })
      .catch((err) => setError(errorMessage(err, "Could not load the admin panel")));
  };

  useEffect(load, []);

  const decide = async (vendor: VendorAdminView, action: "approve" | "reject") => {
    setActingOn(vendor.public_id);
    setNotice(null);
    try {
      const res =
        action === "approve"
          ? await adminApi.approve(vendor.public_id)
          : await adminApi.reject(vendor.public_id);
      setNotice(res.data.message);
      setQueue((q) => q?.filter((v) => v.public_id !== vendor.public_id) ?? null);
      adminApi.stats().then((s) => setStats(s.data)).catch(() => {});
    } catch (err) {
      setError(errorMessage(err, "Could not record that decision"));
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-slate-200 bg-white">
        <div className="container-page flex flex-wrap items-center justify-between gap-3 py-3 sm:h-16 sm:py-0">
          <div className="flex items-center gap-2 sm:gap-4">
            <Logo variant="dark" />
            <span className="rounded-full bg-navy-900 px-2.5 py-1 text-xs font-semibold text-gold-400">
              {user?.full_name}
            </span>
          </div>
          <button onClick={logout} className="btn-outline">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </header>

      <main className="container-page py-8">
        <h1 className="font-display text-3xl font-bold text-navy-900">
          Platform Overview
        </h1>
        <p className="mt-1 text-slate-500">
          Review vendor applications and keep an eye on platform activity.
        </p>

        {error && (
          <div className="mt-5 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <ShieldAlert size={18} /> {error}
          </div>
        )}
        {notice && (
          <div className="mt-5 rounded-lg border border-gold-400 bg-gold-500/10 px-4 py-3 text-sm text-navy-900">
            {notice}
          </div>
        )}

        <StatsGrid stats={stats} />

        <section className="mt-8 rounded-xl border border-slate-200 bg-white p-5 shadow-card">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-navy-900">
              <Building2 size={20} className="text-gold-500" />
              Vendor Verification Queue
            </h2>
            {queue && (
              <span className="rounded-full bg-cream px-3 py-1 text-xs font-medium text-navy-800">
                {queue.length} pending
              </span>
            )}
          </div>

          {queue === null ? (
            <p className="text-sm text-slate-400">Loading applications…</p>
          ) : queue.length === 0 ? (
            <p className="text-sm text-slate-400">
              No pending applications right now — you&apos;re all caught up.
            </p>
          ) : (
            <ul className="space-y-4">
              {queue.map((v) => (
                <VendorRow
                  key={v.public_id}
                  vendor={v}
                  busy={actingOn === v.public_id}
                  onDecide={decide}
                />
              ))}
            </ul>
          )}
        </section>

        <HotelsSection />
      </main>
    </div>
  );
}

function StatsGrid({ stats }: { stats: PlatformStats | null }) {
  const cards = [
    { icon: Users, label: "Total Users", value: stats?.total_users },
    { icon: BadgeCheck, label: "Approved Vendors", value: stats?.total_vendors_approved },
    { icon: Building2, label: "Pending Vendors", value: stats?.total_vendors_pending },
    { icon: Ticket, label: "Total Bookings", value: stats?.total_bookings },
    {
      icon: IndianRupee,
      label: "Revenue",
      value:
        stats?.total_revenue != null
          ? `₹${stats.total_revenue.toLocaleString("en-IN")}`
          : undefined,
    },
    { icon: MapPin, label: "Destinations", value: stats?.total_destinations },
  ];

  return (
    <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div
            key={c.label}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-card"
          >
            <Icon size={20} className="text-gold-500" strokeWidth={1.8} />
            <div className="mt-2 font-display text-xl font-bold text-navy-900">
              {c.value ?? "—"}
            </div>
            <div className="text-xs text-slate-500">{c.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function VendorRow({
  vendor,
  busy,
  onDecide,
}: {
  vendor: VendorAdminView;
  busy: boolean;
  onDecide: (v: VendorAdminView, action: "approve" | "reject") => void;
}) {
  return (
    <li className="rounded-lg border border-slate-100 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-display text-base font-semibold text-navy-900">
              {vendor.name}
            </h3>
            <span className="rounded-full bg-cream px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-navy-700">
              {vendor.type}
            </span>
          </div>
          {vendor.description && (
            <p className="mt-1 max-w-xl text-sm text-slate-500">
              {vendor.description}
            </p>
          )}
          {vendor.address && (
            <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
              <MapPin size={12} /> {vendor.address}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <button
            disabled={busy}
            onClick={() => onDecide(vendor, "approve")}
            className="btn-primary px-3 py-2"
          >
            <CheckCircle2 size={16} /> Approve
          </button>
          <button
            disabled={busy}
            onClick={() => onDecide(vendor, "reject")}
            className="btn-outline px-3 py-2 text-red-600 hover:bg-red-50"
          >
            <XCircle size={16} /> Reject
          </button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 border-t border-slate-100 pt-3 text-sm sm:grid-cols-2">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Applicant
          </div>
          <div className="text-navy-900">{vendor.owner.full_name}</div>
          <div className="text-slate-500">
            {vendor.owner.phone}
            {vendor.owner.email ? ` · ${vendor.owner.email}` : ""}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Compliance documents
          </div>
          <div className="text-slate-600">
            {vendor.fssai_license_id && <div>FSSAI: {vendor.fssai_license_id}</div>}
            {vendor.guide_license_id && <div>Guide license: {vendor.guide_license_id}</div>}
            {!vendor.fssai_license_id && !vendor.guide_license_id && (
              <span className="text-slate-400">None on file</span>
            )}
            <div className="mt-0.5">
              Aadhaar: {vendor.aadhaar_verified ? "Verified" : "Not verified"}
            </div>
          </div>
        </div>
      </div>

      {vendor.services.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
            Listed services
          </div>
          <div className="flex flex-wrap gap-2">
            {vendor.services.map((s) => (
              <span
                key={s.public_id}
                className="rounded-full bg-cream px-3 py-1 text-xs text-navy-800"
              >
                {s.title} · ₹{s.base_price.toFixed(0)}
              </span>
            ))}
          </div>
        </div>
      )}
    </li>
  );
}

/* --------------------------- Manage Hotels ------------------------------ */

const EMPTY_HOTEL = {
  name: "",
  location: "",
  price_per_night: "",
  description: "",
  image_url: "",
};

function HotelsSection() {
  const [hotels, setHotels] = useState<Hotel[] | null>(null);
  const [form, setForm] = useState({ ...EMPTY_HOTEL });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    adminApi
      .listHotels()
      .then((r) => setHotels(asArray<Hotel>(r.data)))
      .catch((e) => setError(errorMessage(e, "Could not load hotels")));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);

    const price = Number(form.price_per_night);
    if (form.name.trim().length < 2) return setError("Hotel name is too short.");
    if (form.location.trim().length < 2) return setError("Please enter a location.");
    if (!Number.isFinite(price) || price <= 0)
      return setError("Enter a valid price per night.");

    setSaving(true);
    try {
      await adminApi.createHotel({
        name: form.name.trim(),
        location: form.location.trim(),
        price_per_night: price,
        description: form.description.trim() || undefined,
        image_url: form.image_url.trim() || undefined,
      });
      setNotice(`Added "${form.name.trim()}".`);
      setForm({ ...EMPTY_HOTEL });
      await load();
    } catch (err) {
      setError(errorMessage(err, "Could not add the hotel"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-8 rounded-xl border border-slate-200 bg-white p-5 shadow-card">
      <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold text-navy-900">
        <HotelIcon size={20} className="text-gold-500" />
        Manage Hotels
      </h2>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-lg border border-gold-400 bg-gold-500/10 px-4 py-2.5 text-sm text-navy-900">
          {notice}
        </div>
      )}

      <form
        onSubmit={submit}
        className="grid grid-cols-1 gap-3 rounded-xl border border-slate-100 bg-cream/60 p-4 sm:grid-cols-2"
      >
        <input
          className="input"
          placeholder="Hotel name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <input
          className="input"
          placeholder="Location (e.g. Jaipur, Rajasthan)"
          value={form.location}
          onChange={(e) => setForm({ ...form, location: e.target.value })}
        />
        <input
          className="input"
          inputMode="numeric"
          placeholder="Price per night (₹)"
          value={form.price_per_night}
          onChange={(e) =>
            setForm({ ...form, price_per_night: e.target.value.replace(/[^\d.]/g, "") })
          }
        />
        <input
          className="input"
          placeholder="Image URL (optional)"
          value={form.image_url}
          onChange={(e) => setForm({ ...form, image_url: e.target.value })}
        />
        <textarea
          className="input sm:col-span-2"
          rows={2}
          placeholder="Description (optional)"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <div className="sm:col-span-2">
          <button disabled={saving} className="btn-primary">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            {saving ? "Adding…" : "Add Hotel"}
          </button>
        </div>
      </form>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-navy-800">Existing hotels</h3>
          {hotels && (
            <span className="rounded-full bg-cream px-3 py-1 text-xs font-medium text-navy-800">
              {hotels.length} total
            </span>
          )}
        </div>
        {hotels === null ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : hotels.length === 0 ? (
          <p className="text-sm text-slate-400">No hotels yet. Add your first above.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {hotels.map((h) => (
              <li key={h.public_id} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <div className="font-medium text-navy-900">{h.name}</div>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <MapPin size={12} /> {h.location}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-xs text-gold-600">
                    <Star size={12} className="fill-gold-500 text-gold-500" />
                    {h.rating.toFixed(1)}
                  </span>
                  <span className="text-sm font-semibold text-navy-900">
                    ₹{h.price_per_night.toLocaleString("en-IN")}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
